import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Inbox, RefreshCcw, Mail, Phone, MapPin, ChevronDown } from "lucide-react";

export default function CoordinatorInbox() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);

  const fetchLeads = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/local/leads");
      setLeads(data.leads || []);
    } catch (e) {
      toast.error("Failed to load leads");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchLeads(); }, []);

  return (
    <section className="glass-strong rounded-sm overflow-hidden" data-testid="coordinator-inbox-section">
      <div className="px-6 py-4 flex items-center justify-between border-b border-white/10">
        <div className="flex items-center gap-3">
          <Inbox size={16} className="text-gold" />
          <div>
            <div className="hud-label">Coordinator Inbox</div>
            <div className="text-[10px] text-slate-500 mt-0.5">
              Audit hand-offs · routed to John Delawder &amp; Parker
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="font-mono-aviation text-xs text-slate-500" data-testid="inbox-count">
            {leads.length} total
          </span>
          <button
            onClick={fetchLeads}
            data-testid="inbox-refresh-button"
            className="btn-ghost-gold px-3 py-1.5 rounded-sm text-xs flex items-center gap-2"
          >
            <RefreshCcw size={12} /> Refresh
          </button>
        </div>
      </div>

      {loading ? (
        <div className="p-10 text-center text-slate-500 text-sm">Loading inbox...</div>
      ) : leads.length === 0 ? (
        <div className="p-12 text-center" data-testid="inbox-empty">
          <Inbox size={28} className="mx-auto mb-3 text-slate-700" />
          <div className="text-sm text-slate-500">No audits yet. The foyer is quiet.</div>
        </div>
      ) : (
        <div className="divide-y divide-white/5" data-testid="inbox-list">
          {leads.map((l) => {
            const open = openId === l.id;
            return (
              <div key={l.id} data-testid={`inbox-row-${l.id}`}>
                <button
                  onClick={() => setOpenId(open ? null : l.id)}
                  className="w-full text-left px-6 py-4 grid grid-cols-12 items-center gap-3 hover:bg-white/5 transition-colors"
                >
                  <div className="col-span-12 sm:col-span-3 flex items-center gap-3 min-w-0">
                    <MapPin size={14} className="text-gold shrink-0" />
                    <div className="min-w-0">
                      <div className="text-sm text-white font-display truncate">{l.city || "—"}</div>
                      <div className="text-[10px] text-slate-500 truncate">{l.industry}</div>
                    </div>
                  </div>
                  <div className="col-span-6 sm:col-span-3">
                    <div className="hud-label text-[9px]">Weekly Leak</div>
                    <div className="font-display text-lg text-gold">${(l.leak_dollars || 0).toLocaleString()}</div>
                  </div>
                  <div className="col-span-6 sm:col-span-2">
                    <div className="hud-label text-[9px]">Score</div>
                    <div className="font-mono-aviation text-sm text-white">{l.leak_score}/100</div>
                  </div>
                  <div className="col-span-10 sm:col-span-3">
                    <div className="hud-label text-[9px]">Submitted</div>
                    <div className="text-xs text-slate-400 font-mono-aviation">
                      {new Date(l.submitted_at).toLocaleString([], {
                        month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
                      })}
                    </div>
                  </div>
                  <div className="col-span-2 sm:col-span-1 flex justify-end">
                    <ChevronDown
                      size={16}
                      className={`text-slate-500 transition-transform ${open ? "rotate-180 text-gold" : ""}`}
                    />
                  </div>
                </button>
                {open && (
                  <div className="px-6 pb-5 pt-1 grid sm:grid-cols-2 gap-4 text-sm" data-testid={`inbox-detail-${l.id}`}>
                    <Field label="Missed Calls / Week" value={l.missed_calls} />
                    <Field label="Reviews" value={l.reviews} />
                    <Field label="Business" value={l.business_name || "—"} />
                    <Field label="Owner" value={l.owner_name || "—"} />
                    <Field label="Email" value={l.email || "—"} icon={Mail} />
                    <Field label="Phone" value={l.phone || "—"} icon={Phone} />
                    <div className="sm:col-span-2 text-[10px] font-mono-aviation text-slate-500 break-all">
                      Ref · {l.id}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

const Field = ({ label, value, icon: Icon }) => (
  <div className="flex items-start gap-2">
    {Icon && <Icon size={12} className="text-slate-500 mt-1" />}
    <div className="min-w-0 flex-1">
      <div className="hud-label text-[9px] mb-0.5">{label}</div>
      <div className="text-sm text-slate-200 truncate">{value}</div>
    </div>
  </div>
);
